import { Injectable } from '@angular/core';
import {
  EExtension,
  Extension,
  FileDetail,
  FileDetailRaw,
  MaterializedFile,
  Media,
} from 'src/app/interfaces/PWA/Files';
import {
  CameraCapabilities,
  CameraCapabilitiesStream,
  MediaCapabilitiesRank,
  REQUIRED_CAPABILITIES_DIMS,
} from 'src/app/interfaces/PWA/CameraCapabilities';
import { Platform } from '@ionic/angular';
import {
  BlackImageError,
  FileSizeLimitError,
  UndecodableFileError,
} from 'src/app/errors/custom';
import { BLACK_PIXEL_RATIO, darkPixelRatio } from 'src/app/utils/ImageQuality';
import { Geolocation } from '@capacitor/geolocation';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  CAMERA_STORAGE_KEY = 'camera_pwa';

  constructor(private readonly platform: Platform) {}

  private get videoConstraints(): MediaTrackConstraints {
    return {
      width: { ideal: REQUIRED_CAPABILITIES_DIMS.WIDTH },
      height: { ideal: REQUIRED_CAPABILITIES_DIMS.HEIGHT },
      facingMode: { exact: 'environment' },
    };
  }

  private get baseCapabilities(): MediaCapabilitiesRank {
    return {
      zoom: false,
      torch: false,
      facingMode: false,
      width: 0,
      height: 0,
    };
  }

  private async createStream(deviceId?: string): Promise<MediaStream> {
    let constraints: MediaStreamConstraints = {};
    if (deviceId) {
      constraints = {
        video: {
          deviceId: deviceId,
          width: { ideal: REQUIRED_CAPABILITIES_DIMS.WIDTH },
          height: { ideal: REQUIRED_CAPABILITIES_DIMS.HEIGHT },
        },
      };
    } else {
      constraints = {
        video: {
          width: { ideal: REQUIRED_CAPABILITIES_DIMS.WIDTH },
          height: { ideal: REQUIRED_CAPABILITIES_DIMS.HEIGHT },
          facingMode: 'environment',
        },
      };
    }
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  private getSavedCamera(): CameraCapabilities | null {
    return JSON.parse(localStorage.getItem(this.CAMERA_STORAGE_KEY) ?? 'null');
  }

  private saveCamera(camera: Omit<CameraCapabilities, 'stream'>) {
    localStorage.setItem(this.CAMERA_STORAGE_KEY, JSON.stringify(camera));
  }

  private async getCameraDevices(
    devices: MediaDeviceInfo[]
  ): Promise<MediaDeviceInfo[]> {
    return devices.filter((device) => device.kind === 'videoinput');
  }

  private async isCameraValid(deviceId: string): Promise<boolean> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(
      (device) => device.deviceId === deviceId && device.kind === 'videoinput'
    );
  }

  public stopStream(stream: MediaStream): void {
    stream.getTracks().forEach((track) => track.stop());
  }

  private rankCapabilities(
    capabilities: MediaTrackCapabilities
  ): MediaCapabilitiesRank {
    return {
      zoom: 'zoom' in capabilities,
      torch: 'torch' in capabilities,
      facingMode: capabilities.facingMode?.includes('environment') ?? false,
      width: capabilities.width?.max ?? 0,
      height: capabilities.height?.max ?? 0,
    };
  }

  private async returnCameraStream(
    camera: CameraCapabilities
  ): Promise<CameraCapabilitiesStream> {
    const stream = await this.createStream(camera.deviceId);
    return { ...camera, stream };
  }

  public async returnDefaultCameraStream(): Promise<CameraCapabilitiesStream> {
    const stream = await this.createStream();
    return {
      deviceId: 'default',
      stream: stream,
      capabilities: {
        zoom: false,
        torch: false,
        facingMode: true,
        width: REQUIRED_CAPABILITIES_DIMS.WIDTH,
        height: REQUIRED_CAPABILITIES_DIMS.HEIGHT,
      },
    };
  }

  public async getBestCameraStream(): Promise<CameraCapabilitiesStream> {
    try {
      const savedCamera = this.getSavedCamera();
      if (savedCamera && (await this.isCameraValid(savedCamera.deviceId))) {
        return await this.returnCameraStream(savedCamera);
      }
      const bestCamera = await this.findBestCamera();
      if (bestCamera) {
        this.saveCamera({
          deviceId: bestCamera.deviceId,
          capabilities: bestCamera.capabilities,
        });
        return await this.returnCameraStream(bestCamera);
      }
      return await this.returnDefaultCameraStream();
    } catch (error) {
      console.error('Error getting camera stream', error);
      return await this.returnDefaultCameraStream();
    }
  }

  public async findBestCamera(): Promise<CameraCapabilities | null> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameraDevices = await this.getCameraDevices(devices);
    let bestCamera: MediaDeviceInfo | null = null;
    let bestCapabilities = this.baseCapabilities;
    for (const device of cameraDevices) {
      const capabilities = await this.getCameraCapabilities(device.deviceId);
      const rankedCapabilities = this.rankCapabilities(capabilities);

      if (this.compareCapabilities(bestCapabilities, rankedCapabilities)) {
        bestCamera = device;
        bestCapabilities = rankedCapabilities;
      }
    }
    return bestCamera
      ? {
          deviceId: bestCamera.deviceId,
          capabilities: bestCapabilities,
        }
      : null;
  }

  private async getCameraCapabilities(
    deviceId: string
  ): Promise<MediaTrackCapabilities> {
    const stream = await this.createStream(deviceId);
    const videoTrack = stream.getVideoTracks()[0];
    const capabilities = videoTrack.getCapabilities();
    this.stopStream(stream);
    videoTrack.stop();
    return capabilities;
  }

  private compareCapabilities(
    current: MediaCapabilitiesRank,
    candidate: MediaCapabilitiesRank
  ): boolean {
    const currentScore = Object.values(current).filter(Boolean).length;
    const candidateScore = Object.values(candidate).filter(Boolean).length;
    return candidateScore > currentScore;
  }

  public async toggleZoom(stream: MediaStream, zoom: number): Promise<void> {
    const videoTrack = (await this.getVideoTrack(stream)) as any;
    videoTrack.applyConstraints({ advanced: [{ zoom }] });
  }

  public async toggleTorch(stream: MediaStream, torch: boolean): Promise<void> {
    const videoTrack = (await this.getVideoTrack(stream)) as any;
    videoTrack.applyConstraints({ advanced: [{ torch }] });
  }

  private async getVideoTrack(stream: MediaStream): Promise<MediaStreamTrack> {
    return stream.getVideoTracks()[0];
  }

  /**
   * Límites máximos de peso por extensión (MB). Definición de negocio LET:
   * PDF 10 MB · fotos 8 MB.
   * Las imágenes (jpg/jpeg/png) se validan DESPUÉS de la compresión en
   * cliente (PhotoStorageService.prepareImageForUpload); el PDF se valida
   * al seleccionar. Un archivo sobre el límite no se sube y se informa al
   * usuario mediante alerta.
   */
  public static readonly MAX_FILE_SIZE_MB: Partial<Record<Extension, number>> = {
    pdf: 10,
    jpg: 8,
    jpeg: 8,
    png: 8,
  };

  /** Extensiones que se comprimen en cliente: su límite aplica post-compresión. */
  public static readonly COMPRESSIBLE_EXTENSIONS: Extension[] = [
    'jpg',
    'jpeg',
    'png',
  ];

  public checkFileDetail(
    file: File,
    accept: Extension[],
    maxSizeMB?: number
  ): FileDetailRaw {
    const ext = this.getFileExt(file);
    if (!accept.includes(ext)) {
      throw new Error(
        `El archivo debe tener alguna de las siguientes extensiones: ${accept.join(
          ', '
        )}`
      );
    }
    if (!file.size) {
      throw new Error(
        'El archivo está vacío (0 bytes). Selecciónalo nuevamente.'
      );
    }
    // Las imágenes se validan después de comprimir (prepareImageForUpload);
    // aquí solo se limita lo no-comprimible (pdf).
    const isCompressible = FileService.COMPRESSIBLE_EXTENSIONS.includes(ext);
    const limitMB = maxSizeMB ?? FileService.MAX_FILE_SIZE_MB[ext];
    if (!isCompressible && limitMB && file.size > limitMB * 1024 * 1024) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      throw new FileSizeLimitError(
        `El archivo pesa ${sizeMB} MB y supera el máximo permitido de ${limitMB} MB para archivos .${ext}. Selecciona un archivo más liviano.`
      );
    }
    const fileDetail = {
      data: file,
      ext: ext,
    };
    return fileDetail;
  }

  /**
   * Copia los bytes del archivo a memoria.
   *
   * En WebKit —o sea todo navegador de iOS— un `File` del selector es un handle al archivo en disco:
   * resetear el input libera ese respaldo y el PUT posterior sube 0 bytes, con los metadatos ya
   * cacheados de modo que la validación pasa igual. Hay que copiar ANTES de limpiar el input.
   *
   * Puede fallar por causas del dispositivo, la más común un archivo de iCloud sin descargar.
   */
  public async copyFileToMemory(file: File): Promise<MaterializedFile> {
    try {
      const buffer = await file.arrayBuffer();
      // Los dos tamaños solo coexisten en este instante: después de la copia, el `.size` del File
      // nuevo YA es el real y el declarado se pierde. La discrepancia entre ambos es el síntoma
      // del caso iOS de referencia stale, y medirla en otro lado es imposible.
      return {
        file: new File([buffer], file.name, { type: file.type }),
        declaredSizeBytes: file.size,
        realSizeBytes: buffer.byteLength,
      };
    } catch {
      throw new Error(
        'No se pudo leer el archivo seleccionado. Si está en iCloud, descárgalo primero e inténtalo de nuevo.'
      );
    }
  }

  /**
   * Materializa el contenido del archivo y lo examina antes de pedir la URL de subida.
   *
   * Se invoca sobre el blob **que se va a subir**, ya comprimido, y no sobre el original. Es la
   * única forma de tener paridad exacta con el servidor, que analiza justamente ese objeto: la
   * codificación JPEG agrega ruido que aleja los píxeles del negro puro, así que medir el original
   * —más oscuro o igual— podría rechazar una foto que el servidor después acepta, que es la
   * dirección prohibida.
   *
   * De una sola decodificación salen las dos respuestas. Y sirve además como control de legibilidad
   * aunque `compressImage` se trague su propia falla: cuando no puede decodificar devuelve el
   * original, así que el blob que llega acá es el mismo que falló y vuelve a fallar, ahora con un
   * motivo explícito en vez de subirse igual.
   */
  public async assertImageContentIsUploadable(media: Blob): Promise<number> {
    let pixels: ImageData;
    try {
      pixels = await this.decodeForAnalysis(media);
    } catch {
      throw new UndecodableFileError(
        'No se pudo leer la imagen. Vuelve a capturarla o selecciona otro archivo.'
      );
    }
    const ratio = darkPixelRatio(pixels);
    if (ratio > BLACK_PIXEL_RATIO) {
      throw new BlackImageError(
        'La foto salió completamente oscura. Revisa que el lente no esté tapado y vuelve a tomarla.'
      );
    }
    // Se devuelve el número y no un booleano: para el informe de causa raíz de 426, «qué tan oscura»
    // dice mucho más que «negra sí o no», y el cálculo ya está hecho.
    return ratio;
  }

  /**
   * Dibuja el blob sobre **fondo blanco**, a tamaño natural y sin reescalar.
   *
   * No se reduce a propósito. Promediar a una grilla chica **no** es conservador: en una imagen
   * uniformemente tenue apenas sobre el umbral —el ruido de una foto con el lente tapado— cada
   * bloque promedia por debajo y el cliente la marca negra cuando el servidor no lo hace.
   * Verificado ejecutando las dos fórmulas; el caso vive en `scripts/verify-image-quality.cjs`.
   *
   * El fondo importa y no es estético: un canvas nace transparente, o sea en (0,0,0,0), mientras
   * que el `convert("RGB")` del servidor descarta el alfa y conserva el RGB original. Sin pintar,
   * un PNG transparente se leería como negro acá y como su color real allá — el cliente quedaría
   * más estricto que el servidor, que es lo único que este código no puede permitirse.
   */
  private async decodeForAnalysis(media: Blob): Promise<ImageData> {
    const bitmap = await createImageBitmap(media);
    try {
      const width = bitmap.width;
      const height = bitmap.height;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        throw new Error('El canvas no entregó contexto 2D');
      }
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap, 0, 0, width, height);
      return context.getImageData(0, 0, width, height);
    } finally {
      bitmap.close();
    }
  }

  /**
   * Comprime una imagen en el cliente antes de subirla: redimensiona a un
   * máximo de `maxDimension` px (lado mayor) y re-encoda a JPEG con la
   * calidad indicada. Mismo criterio que aplicaba `sharp` en el backend
   * (2048 px / q80) antes de migrar a URLs prefirmadas.
   * Si la compresión falla o no reduce el peso, retorna el blob original.
   */
  public async compressImage(
    media: Blob,
    maxDimension = 2048,
    quality = 0.8
  ): Promise<Blob> {
    try {
      const bitmap = await createImageBitmap(media);
      const scale = Math.min(
        1,
        maxDimension / Math.max(bitmap.width, bitmap.height)
      );
      const width = Math.round(bitmap.width * scale);
      const height = Math.round(bitmap.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        return media;
      }
      context.drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      const compressed = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality)
      );
      if (!compressed || compressed.size >= media.size) {
        return media;
      }
      return compressed;
    } catch (error) {
      console.warn('[COMPRESS] No se pudo comprimir la imagen, se sube original:', error);
      return media;
    }
  }

  public takePhoto(video: HTMLVideoElement): string {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('No se pudo obtener el contexto del canvas');
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg');
    return dataUrl;
  }


  public async initRecorder({
    stream,
  }: {
    stream: MediaStream;
  }): Promise<MediaRecorder> {
    const mimeTypes = [
      'video/webm;codecs=vp9',
      'video/mp4;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/mp4;codecs=avc1',
    ];

    // Default option
    let options = { mimeType: 'video/webm;codecs=vp9' };

    // On iOS, use mime type likely to be supported
    if (this.platform.is('ios')) {
      options = { mimeType: 'video/mp4;codecs=avc1' };
    } else if (this.platform.is('android')) {
      // Android often supports VP8
      options = { mimeType: 'video/webm;codecs=vp8' };
    }

    let mediaRecorder: MediaRecorder;
    try {
      mediaRecorder = new MediaRecorder(stream, options);
    } catch (error) {
      console.error(
        `Error creating MediaRecorder with options ${JSON.stringify(
          options
        )}: `,
        error
      );
      throw error;
    }

    // Do not attach event listeners or start the recorder here.
    // The caller will attach its own event handlers and call start().

    return mediaRecorder;
  }

  public async onStopRecorder(chunks: Blob[]) {
    const videoBuffer = new Blob(chunks, { type: 'video/mp4' });
    return videoBuffer;
  }

  public async stopRecorder(mediaRecorder: MediaRecorder) {
    if (mediaRecorder) {
      mediaRecorder.stop();
    }
  }

  public async b64toBlob(b64Data: string): Promise<Blob> {
    const response = await fetch(b64Data);
    const blob = await response.blob();
    return blob;
  }

  public async getBlobUrl(blob: Blob): Promise<string> {
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  }

  public revokeBlobUrl(blobUrl: string) {
    URL.revokeObjectURL(blobUrl);
  }

  public getFileExt(file: File): Extension {
    // iOS entrega extensiones en mayúsculas (IMG_0042.JPG, DOCUMENTO.PDF);
    // el accept del backend viene en minúsculas.
    return file.name.split('.').pop()?.toLowerCase() as Extension;
  }

  public downloadFile(file: FileDetail, fileName: string) {
    if (file.ext !== EExtension.pdf) {
      throw new Error('El archivo no es un PDF');
    }
    const url = file.data;
    window.open(url, '_blank');
  }

  public async getFrontCameraStream(): Promise<CameraCapabilitiesStream> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const frontCamera = devices.find(
      (device) =>
        device.kind === 'videoinput' &&
        device.label.toLowerCase().includes('front')
    );
    if (!frontCamera) {
      return this.returnDefaultCameraStream();
    }
    const stream = await this.createStream(frontCamera.deviceId);
    return {
      deviceId: frontCamera.deviceId,
      stream: stream,
      capabilities: {
        zoom: false,
        torch: false,
        facingMode: true,
        width: REQUIRED_CAPABILITIES_DIMS.WIDTH,
        height: REQUIRED_CAPABILITIES_DIMS.HEIGHT,
      },
    };
  }

  public async hasFrontCamera(): Promise<boolean> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(
      (device) =>
        device.kind === 'videoinput' &&
        device.label.toLowerCase().includes('front')
    );
  }

  public async getDeviceLocation(): Promise<{
    latitude: number;
    longitude: number;
  } | null> {
    try {
      const position = await Geolocation.getCurrentPosition();
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (error) {
      console.error('Error getting device location:', error);
      return null;
    }
  }
}
